import { flagKindReach, type FlagKind } from '../schemas/flag-type';

/**
 * What the prod-write confirmation dialog says, for a platform-default write
 * (NOXSCRUM-1529).
 *
 * The confirmation states the **blast radius being written**. Each backend
 * environment has its own Backoffice deployment and its own flags table
 * (ADR 0009), so a default written here reaches every museum in the environment
 * this Backoffice is deployed in, and no other. The copy names that environment
 * — the label the caller passes, read from the build's `VITE_BACKOFFICE_ENV` —
 * rather than a partition: there are no per-environment partitions any more
 * (NOXSCRUM-1656).
 *
 * Every variant also names the **kind** of flag (ADR 0009 §5, NOXSCRUM-1670):
 * a `salesforce` default reaches customers' orgs, a `veevart2` one never
 * leaves the platform, and those are different things to agree to.
 *
 * A pure function rather than JSX so it is testable without a DOM — this
 * repo's `apps/web` suite runs under `environment: 'node'`, no jsdom.
 */

export type PlatformDefaultChange =
    | { readonly field: 'enabled'; readonly next: boolean }
    | { readonly field: 'archived'; readonly next: boolean };

function blastRadius(environmentLabel: string): string {
    return (
        `This is the platform default in ${environmentLabel} — it affects every museum ` +
        `in ${environmentLabel}, and no other environment.`
    );
}

export function platformDefaultConfirmCopy(
    flagKey: string,
    change: PlatformDefaultChange,
    kind: FlagKind,
    environmentLabel: string,
): { readonly title: string; readonly description: string } {
    const reach = flagKindReach(kind);
    const radius = blastRadius(environmentLabel);

    if (change.field === 'archived') {
        return change.next
            ? {
                  title: `Archive ${flagKey} in ${environmentLabel}?`,
                  description:
                      `This is the real kill switch: ${flagKey} will resolve off ` +
                      `for every museum in ${environmentLabel} — including any museum ` +
                      `that currently overrides it — until it is un-archived. ${reach} ${radius}`,
              }
            : {
                  title: `Un-archive ${flagKey} in ${environmentLabel}?`,
                  description:
                      `${flagKey} resolves off in ${environmentLabel} while archived. Un-archiving ` +
                      `restores its stored value and any overrides — nothing else about ` +
                      `them changes. ${reach} ${radius}`,
              };
    }

    return {
        title: `Set the platform default of ${flagKey} to ${change.next ? 'on' : 'off'} in ${environmentLabel}?`,
        description:
            `Changes what ${flagKey} resolves to for every museum with no override ` +
            `of their own. ${reach} ${radius}`,
    };
}
